import _ from 'lodash';
import { mediaAssetsService } from '../../transport/index';
import { GetFilesListMetaParams } from '../../transport/services/media-assets.service';
import {
  GetFilesListResponse,
  GetFilesResponse,
  LoadFilesRequestData,
  NavigateToFolder,
  OpenFolderResponse,
} from '../../types/media-assets';
import { normalizeFile } from '../../utils/media-assets/media-assets';
import { join } from '../../utils/path/path';
import { createFolder } from '../files/files.actions';
import { StoreState } from '../reducers/store.types';
import { Dispatch, GetState, ThunkAction } from '../redux.types';
import {
  getColumnsSorting,
  getTableSorting,
} from '../sorted-columns/sorted-columns.selectors';
import {
  getCurrentPathCached,
  getPaginationParamsCached,
} from './file-manager-component.selectors';

export const FILE_MANAGER_COMPONENT_ACTIONS = {
  INIT_COMPONENT: 'FILE_MANAGER_COMPONENT_ACTIONS.INIT_COMPONENT',
  FILES_LIST_GET_START: 'MEDIA_ASSETS.FILES_LIST.GET.START',
  FILES_LIST_GET_SUCCESS: 'MEDIA_ASSETS.FILES_LIST.GET.SUCCESS',
  FILES_LIST_GET_ERROR: 'MEDIA_ASSETS.FILES_LIST.GET.FAILED',

  FILES_LIST_LOAD_MORE_START: 'MEDIA_ASSETS.FILES_LIST.LOAD_MORE.START',
  FILES_LIST_LOAD_MORE_SUCCESS: 'MEDIA_ASSETS.FILES_LIST.LOAD_MORE.SUCCESS',
  FILES_LIST_LOAD_MORE_ERROR: 'MEDIA_ASSETS.FILES_LIST.LOAD_MORE.ERROR',

  SHOW_CREATE_NEW_FOLDER_INPUT:
    'FILE_MANAGER_ACTIONS__SHOW_CREATE_NEW_FOLDER_INPUT',
  HIDE_CREATE_NEW_FOLDER_INPUT:
    'FILE_MANAGER_ACTIONS__HIDE_CREATE_NEW_FOLDER_INPUT',

  SET_CURRENT_PATH_IN_MEMORY:
    'MEDIA_ASSETS_ACTIONS__SET_CURRENT_PATH_IN_MEMORY',
};

export const initFileManagerComponent = (componentId: string) => ({
  type: FILE_MANAGER_COMPONENT_ACTIONS.INIT_COMPONENT,
  payload: undefined,
  meta: {
    componentId,
  },
});

const getListFilesStart = (componentId: string) => ({
  type: FILE_MANAGER_COMPONENT_ACTIONS.FILES_LIST_GET_START,
  payload: undefined,
  meta: {
    componentId,
  },
});
const getListFilesSuccess = (
  payload: OpenFolderResponse,
  componentId: string,
) => ({
  type: FILE_MANAGER_COMPONENT_ACTIONS.FILES_LIST_GET_SUCCESS,
  payload,
  meta: {
    componentId,
  },
});
const getListFilesFailed = (reason: any, componentId: string) => ({
  type: FILE_MANAGER_COMPONENT_ACTIONS.FILES_LIST_GET_ERROR,
  payload: reason,
  meta: {
    componentId,
  },
});

/**
 * Loads very first files for a given path and sets current folder in Store
 * When you need to load more use different method
 */
export const getListFiles =
  (
    { path, organizationId, project, type, locateFileId }: NavigateToFolder,
    componentId: string,
  ): ThunkAction<Promise<GetFilesListResponse>> =>
  (dispatch: Dispatch, getState: GetState) => {
    dispatch(getListFilesStart(componentId));

    const state = getState();
    const metaParams = getFilesListMetaParams(state, componentId);

    return mediaAssetsService
      .getListFiles(
        { path, organizationId, project },
        { ...metaParams, type, nextPageToken: undefined },
      )
      .then(
        async (response: GetFilesListResponse) => {
          const { files, nextPageToken } = response;

          if (locateFileId && !files.find(({ id }) => id === locateFileId)) {
            const { fileDescriptor } = await mediaAssetsService.getFileById({
              id: locateFileId,
              organizationId,
              project,
            });
            files.unshift(fileDescriptor);
          }

          dispatch(
            getListFilesSuccess(
              {
                path,
                files: _.map(files, normalizeFile),
                nextPageToken,
                locateFileId,
              },
              componentId,
            ),
          );

          return response;
        },
        (reason) => {
          dispatch(getListFilesFailed(reason, componentId));
          return Promise.reject(reason);
        },
      );
  };

const loadMoreFilesStart = (componentId: string) => ({
  type: FILE_MANAGER_COMPONENT_ACTIONS.FILES_LIST_LOAD_MORE_START,
  payload: undefined,
  meta: {
    componentId,
  },
});

const loadMoreFilesSuccess = (
  payload: GetFilesResponse,
  componentId: string,
) => ({
  type: FILE_MANAGER_COMPONENT_ACTIONS.FILES_LIST_LOAD_MORE_SUCCESS,
  payload,
  meta: {
    componentId,
  },
});

const loadMoreFilesFailed = (reason: any, componentId: string) => ({
  type: FILE_MANAGER_COMPONENT_ACTIONS.FILES_LIST_LOAD_MORE_ERROR,
  payload: reason,
  meta: {
    componentId,
  },
});

export const loadMoreFiles =
  (
    { path, organizationId, project }: LoadFilesRequestData,
    componentId: string,
  ) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const metaParams = getFilesListMetaParams(state, componentId);

    dispatch(loadMoreFilesStart(componentId));

    return mediaAssetsService
      .getListFiles({ path, organizationId, project }, metaParams)
      .then(
        ({ files, nextPageToken }: GetFilesListResponse) => {
          dispatch(
            loadMoreFilesSuccess(
              {
                path,
                files: _.map(files, normalizeFile),
                nextPageToken,
              },
              componentId,
            ),
          );
        },
        (reason) => dispatch(loadMoreFilesFailed(reason, componentId)),
      );
  };

export function getFilesListMetaParams(
  state: StoreState,
  componentId: string,
): GetFilesListMetaParams {
  const sort = getTableSorting(getColumnsSorting(state), componentId);
  const pagination = getPaginationParamsCached(state, componentId);

  return {
    sort,
    pageSize: pagination.pageSize,
    nextPageToken: pagination.nextPageToken,
  };
}

export const setCurrentPathInMemory = (path: string, componentId: string) => ({
  type: FILE_MANAGER_COMPONENT_ACTIONS.SET_CURRENT_PATH_IN_MEMORY,
  payload: path,
  meta: {
    componentId,
  },
});

export const showCreateNewFolderInput = (componentId: string) => ({
  type: FILE_MANAGER_COMPONENT_ACTIONS.SHOW_CREATE_NEW_FOLDER_INPUT,
  payload: undefined,
  meta: {
    componentId,
  },
});

export const hideCreateNewFolderInput = (componentId: string) => ({
  type: FILE_MANAGER_COMPONENT_ACTIONS.HIDE_CREATE_NEW_FOLDER_INPUT,
  payload: undefined,
  meta: {
    componentId,
  },
});

export const createFolderInCurrentPath =
  (componentId: string, folderName: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    const currentPath = getCurrentPathCached(state, componentId);
    const fullPath = join(currentPath, folderName);

    return createFolder(fullPath)(dispatch, getState);
  };
